import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../auth/authContext.tsx";
import type { WebAdminPage, WebAdminPageMenuItem } from "../../types/session.ts";

/** Display-name trail for a page, outermost menu ancestor first. Also used by the
 *  page-help button to tell Issa where the page lives in the menu. */
export function buildBreadcrumb(
  pageName: string,
  pages: WebAdminPage[],
  menuItems: WebAdminPageMenuItem[]
): string[] {
  // Find the page by Name (case-insensitive)
  const page = pages.find(
    (p) => p.Name.toLowerCase() === pageName.toLowerCase()
  );
  if (!page) return [pageName];

  // Find the menu item for this page (by RowID)
  const menuItem = menuItems.find((mi) => mi.RowID === page.RowID);
  if (!menuItem) return [page.DisplayName];

  // Walk up parent chain
  const chain: string[] = [page.DisplayName];
  let parentID = menuItem.ParentID;

  while (parentID > 0) {
    const parentMenuItem = menuItems.find((mi) => mi.ID === parentID);
    if (!parentMenuItem) break;
    const parentPage = pages.find((p) => p.RowID === parentMenuItem.RowID);
    if (parentPage) {
      chain.unshift(parentPage.DisplayName);
    }
    parentID = parentMenuItem.ParentID;
  }

  return chain;
}

export function resolvePageName(pathname: string, search: string): string | null {
  const params = new URLSearchParams(search);
  if (pathname === "/grid") {
    return params.get("name");
  }
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length > 0) {
    // For /page/:name routes
    if (segments[0] === "page" && segments[1]) {
      return segments[1];
    }
    // For /simple/:name routes (kiosk, operator, transaction)
    if (segments[0] === "simple" && segments[1]) {
      return segments[1];
    }
  }
  // Dashboard (root)
  if (pathname === "/") {
    return "dashboard";
  }
  return null;
}

export function Breadcrumb() {
  const { cache, activeEndpoint } = useAuth();
  const { pathname, search } = useLocation();

  const endpointTitle = activeEndpoint?.Title ?? "";

  const pages = useMemo(() => cache?.PageInfo?.Page ?? [], [cache]);
  const menuItems = useMemo(() => cache?.PageInfo?.MenuItem ?? [], [cache]);

  const pageName = resolvePageName(pathname, search);

  const trail = useMemo(() => {
    if (!pageName) return [];
    return buildBreadcrumb(pageName, pages, menuItems);
  }, [pageName, pages, menuItems]);

  if (!trail.length) {
    return <span>{endpointTitle}</span>;
  }

  return (
    <span>
      {endpointTitle} | {trail.join("  \u2192  ")}
    </span>
  );
}
